import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, X, Store, Tag, Inbox, User, Loader2 } from "lucide-react";

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ partners: [], offers: [], requests: [], users: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Keyboard shortcut: Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { if (open) setTimeout(()=>inputRef.current?.focus(), 50); }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setData({ partners: [], offers: [], requests: [], users: [] }); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q.trim())}`);
        setData(data);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };

  const total = data.partners.length + data.offers.length + data.requests.length + data.users.length;

  return (
    <>
      <button onClick={()=>setOpen(true)} data-testid="admin-global-search-btn"
              className="flex items-center gap-2 bg-slate-800/70 hover:bg-slate-700 border border-slate-700 text-slate-300 hover:text-white px-3 py-1.5 rounded-lg text-xs w-full">
        <Search size={14}/>
        <span className="flex-1 text-left">Rechercher…</span>
        <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-slate-900 border border-slate-700 font-mono">⌘K</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-4 md:pt-24" onClick={()=>setOpen(false)} data-testid="global-search-modal">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-center gap-3 border-b border-slate-100 p-4">
              <Search size={18} className="text-slate-400"/>
              <input ref={inputRef} value={q} onChange={(e)=>setQ(e.target.value)}
                     placeholder="Partenaire, offre, demande, email…"
                     className="flex-1 outline-none text-lg" data-testid="global-search-input"/>
              {loading && <Loader2 size={16} className="animate-spin text-slate-400"/>}
              <button onClick={()=>setOpen(false)} className="p-1 rounded hover:bg-slate-100"><X size={16}/></button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {q.trim().length < 2 && (
                <div className="p-6 text-center text-sm text-slate-500">Tapez au moins 2 caractères pour rechercher.</div>
              )}
              {q.trim().length >= 2 && !loading && total === 0 && (
                <div className="p-6 text-center text-sm text-slate-500">Aucun résultat pour « {q} ».</div>
              )}

              {data.partners.length > 0 && (
                <Section title="Partenaires" icon={Store}>
                  {data.partners.map((p) => (
                    <button key={p.id} onClick={()=>go(`/admin/professionnels?open=${p.id}`)}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-3"
                            data-testid={`search-partner-${p.id}`}>
                      <Store size={14} className="text-slate-400"/>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{p.name}</div>
                        <div className="text-xs text-slate-500 truncate">{p.email || "—"} · {p.city || "—"}</div>
                      </div>
                      {p.crm_status && <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full">{p.crm_status}</span>}
                    </button>
                  ))}
                </Section>
              )}

              {data.offers.length > 0 && (
                <Section title="Offres" icon={Tag}>
                  {data.offers.map((o) => (
                    <button key={o.id} onClick={()=>go(`/admin/offres/${o.id}/editer`)}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-3"
                            data-testid={`search-offer-${o.id}`}>
                      <Tag size={14} className="text-slate-400"/>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{o.title}</div>
                        <div className="text-xs text-slate-500 truncate">{o.city || "—"}</div>
                      </div>
                      <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full">{o.status}</span>
                    </button>
                  ))}
                </Section>
              )}

              {data.requests.length > 0 && (
                <Section title="Demandes" icon={Inbox}>
                  {data.requests.map((r) => (
                    <button key={r.id} onClick={()=>go(`/admin/demandes`)}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-3"
                            data-testid={`search-request-${r.id}`}>
                      <Inbox size={14} className="text-slate-400"/>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{r.name} · {r.email}</div>
                        <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString("fr-FR")}</div>
                      </div>
                      <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full">{r.status}</span>
                    </button>
                  ))}
                </Section>
              )}

              {data.users.length > 0 && (
                <Section title="Utilisateurs" icon={User}>
                  {data.users.map((u) => (
                    <button key={u.id} onClick={()=>go(`/admin/utilisateurs`)}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-3"
                            data-testid={`search-user-${u.id}`}>
                      <User size={14} className="text-slate-400"/>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{u.first_name} {u.last_name}</div>
                        <div className="text-xs text-slate-500 truncate">{u.email}</div>
                      </div>
                      <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded-full">{u.role}</span>
                    </button>
                  ))}
                </Section>
              )}
            </div>

            <div className="border-t border-slate-100 px-4 py-2 text-[10px] text-slate-400 flex justify-between">
              <span>Astuce : <kbd className="font-mono px-1 bg-slate-100 rounded">⌘K</kbd> pour ouvrir · <kbd className="font-mono px-1 bg-slate-100 rounded">Esc</kbd> pour fermer</span>
              {total > 0 && <span>{total} résultat(s)</span>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1.5">
        <Icon size={12}/> {title}
      </div>
      {children}
    </div>
  );
}
